import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Divider, Select } from '@/components/ui/daisy';
import { useQuestionFormsOrchestrator } from '@/data/context/QuestionFormsOrchestrator';
import { useFormRegistration } from '@/data/hook/question/useFormRegistration';
import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import { PointsSection } from '../fields/PointsSection';

import { FeedbackCreateDTO } from '@/models/Feedback';
import { TrueOrFalseQuestion } from '@/models/Question';
import {
  DEFAULT_LANGUAGE,
  SupportedLanguage,
  TranslatableDict,
} from '@/models/TranslatableField';
import { adapterFeedbacks } from '@/utils/questions/adapterFeedbacks';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import { QuestionFormContainer } from '@/components/question/forms/QuestionFormContainer';
import { QuestionContextField } from '@/components/question/forms/fields/QuestionContextField';
import { QuestionTitleField } from '@/components/question/forms/fields/QuestionTitleField';
import QuestionPreview from '@/components/question/forms/QuestionPreview';
import adapterTrueOrFalseForm from '@/utils/questions/adapterTrueOrFalseForms';

export type TrueOrFalseFormFields = {
  title: TranslatableDict;
  context: TranslatableDict;
  isTrue: boolean;
  feedbacks: FeedbackCreateDTO[];
  maxGrade?: number;
};

type TrueOrFalseFormProps = {
  isActive?: boolean;
  question: TrueOrFalseQuestion;
  isLoading?: boolean;
  onSubmit: (data: TrueOrFalseFormFields) => Promise<TrueOrFalseQuestion>;
};

export default function TrueOrFalseForm({
  isActive = true,
  question,
  isLoading,
  onSubmit,
}: TrueOrFalseFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'courseLineForm.questionsTab.createQuestionModal.trueOrFalseForm',
  });
  const { t: tAnswers } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });

  const { identifier } = question;

  const [activeLanguage, setActiveLanguage] =
    useState<SupportedLanguage>(DEFAULT_LANGUAGE);

  const gradeCtx = useQuestionExamGradeCtx();
  const maxGradeDefault = gradeCtx?.getGradeForQuestion(question)?.maxGrade;

  const defaultValues = useMemo<TrueOrFalseFormFields>(
    () => ({
      title: normalizeTranslatableField(question?.title),
      context: normalizeTranslatableField(question?.context),
      isTrue: question?.isTrue ?? true,
      feedbacks: adapterFeedbacks(question?.feedbacks ?? []),
      maxGrade: maxGradeDefault,
    }),
    [question, maxGradeDefault],
  );

  const { submitForm } = useQuestionFormsOrchestrator();

  const methods = useForm<TrueOrFalseFormFields>({ defaultValues });
  const { watch, register, trigger, reset, formState } = methods;

  const realTimeValues = watch();
  const previewQuestion = adapterTrueOrFalseForm(realTimeValues);

  const { hasChanges, handleReset } = useFormRegistration({
    identifier,
    submit: onSubmit,
    validate: trigger,
    watch,
    formState,
    reset,
    defaultValues,
  });

  return (
    <div className="flex gap-6">
      <QuestionFormContainer
        methods={methods}
        activeLanguage={activeLanguage}
        onLanguageChange={setActiveLanguage}
        onSubmit={e => {
          e.preventDefault();
          submitForm(identifier);
        }}
        isLoading={isLoading}
        hasChanges={hasChanges}
        onCancel={handleReset}
      >
        <div className="flex flex-col gap-4">
          <QuestionTitleField
            activeLanguage={activeLanguage}
            placeholder={t('titlePlaceholder')}
          />

          <QuestionContextField
            activeLanguage={activeLanguage}
            isActive={isActive}
          />

          <Divider />
          <h3 className="text-base font-semibold text-base-content">
            {tAnswers('answersTitle')}
          </h3>
          <Select.Fieldset label={t('whatIsTheAnswer')}>
            <Select
              className="w-full"
              {...register('isTrue', { setValueAs: v => v === 'true' })}
            >
              <Select.Option value="true">{t('true')}</Select.Option>
              <Select.Option value="false">{t('false')}</Select.Option>
            </Select>
          </Select.Fieldset>
        </div>

        <PointsSection />
      </QuestionFormContainer>

      <div className="flex-1">
        <QuestionPreview question={previewQuestion} />
      </div>
    </div>
  );
}
